import { useI18n } from '@/hooks/useI18n';
import { useMemo } from 'react';
import { ExternalLink, Eye, Flame, Layers, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Chip } from '@/components/primitives/Chip';
import { Empty } from '@/components/primitives/Empty';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { useTemplateDetail } from '@/hooks/queries/useTemplates';
import { useIdentities } from '@/hooks/queries/useIdentities';
import { formatDate, formatNumber, formatPct } from '@/lib/format';
import { buildReelUrl } from '@/lib/ig';
import { proxyImage } from '@/lib/imageProxy';
import type {
  IdentityResponse,
  ReelResponse,
  TemplateAccountBreakdown,
  TemplateDetail,
} from '@/types/api';

interface TemplateDetailDialogProps {
  templateHash: string | null;
  periodDays: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TemplateDetailDialog({
  templateHash,
  periodDays,
  open,
  onOpenChange,
}: TemplateDetailDialogProps) {
  const { t } = useI18n();
  const detail = useTemplateDetail(templateHash ?? undefined, periodDays);
  const identities = useIdentities();

  const identityMap = useMemo(() => {
    const map = new Map<string, IdentityResponse>();
    (identities.data ?? []).forEach((i) => map.set(i.id, i));
    return map;
  }, [identities.data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl overflow-hidden border-line-1 bg-bg-1 p-0">
        <DialogTitle className="sr-only">{t('Détail du template', 'Template details')}</DialogTitle>

        {detail.isLoading ? (
          <LoadingBody />
        ) : detail.error ? (
          <div className="p-6">
            <ErrorInline error={detail.error} onRetry={() => detail.refetch()} />
          </div>
        ) : detail.data ? (
          <Body detail={detail.data} identityMap={identityMap} periodDays={periodDays} />
        ) : (
          <div className="p-6">
            <Empty title={t('Template introuvable', 'Template not found')} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LoadingBody() {
  return (
    <div className="grid gap-0 md:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-4 p-6">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-28 w-full rounded-[12px]" />
        <Skeleton className="h-40 w-full rounded-[12px]" />
      </div>
      <div className="space-y-4 border-t border-line-1 p-6 md:border-l md:border-t-0">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-20 w-full rounded-[12px]" />
        <Skeleton className="h-20 w-full rounded-[12px]" />
      </div>
    </div>
  );
}

interface BodyProps {
  detail: TemplateDetail;
  identityMap: Map<string, IdentityResponse>;
  periodDays: number;
}

function Body({ detail, identityMap, periodDays }: BodyProps) {
  const { t } = useI18n();
  const { caption, summary, byAccount, reels } = detail;

  return (
    <div className="grid max-h-[85dvh] min-w-0 gap-0 overflow-y-auto md:grid-cols-[minmax(0,1fr)_360px] md:overflow-hidden">
      <div className="flex min-w-0 flex-col gap-5 p-4 md:overflow-y-auto md:p-6">
        <header className="space-y-2 pr-6">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 shrink-0 text-violet-deep" />
            <span className="font-tech min-w-0 break-all text-[11px] uppercase tracking-[0.18em] text-text-3">
              Template · {summary.templateHash}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip tone="violet">
              <Eye className="h-2.5 w-2.5" />
              {t('Médiane', 'Median')} · {formatNumber(summary.medianViews)}
            </Chip>
            <Chip>
              <Layers className="h-2.5 w-2.5" />
              {summary.reelCount} reels
            </Chip>
            <Chip>
              <Users className="h-2.5 w-2.5" />
              {summary.accountCount}{' '}
              {summary.accountCount > 1 ? t('comptes', 'accounts') : t('compte', 'account')}
            </Chip>
            {summary.viralCount > 0 && (
              <Chip tone="violet">
                <Flame className="h-2.5 w-2.5" />
                {summary.viralCount} viral ·{' '}
                {formatPct(summary.viralRate, { sign: false, digits: 0 })}
              </Chip>
            )}
          </div>
        </header>

        <StatsRow summary={summary} />

        <section className="space-y-2">
          <SectionTitle>{t('Caption complète', 'Full caption')}</SectionTitle>
          <div className="max-h-60 overflow-y-auto rounded-[12px] border border-line-1 bg-bg-2 p-4">
            <p className="whitespace-pre-line break-words font-mono text-[12px] leading-relaxed text-text-2">
              {caption ?? t('(Sans caption)', '(No caption)')}
            </p>
          </div>
        </section>

        <section className="space-y-2">
          <SectionTitle>{t('Performance par compte', 'Performance by account')}</SectionTitle>
          <AccountBreakdownTable rows={byAccount} identityMap={identityMap} />
        </section>
      </div>

      <aside className="flex min-w-0 flex-col border-t border-line-1 md:overflow-hidden md:border-l md:border-t-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-1 px-4 py-3 md:px-5">
          <SectionTitle>{t('Reels du template', 'Template reels')}</SectionTitle>
          <span className="text-[11px] text-text-4">
            {reels.length}{' '}
            {periodDays === 0
              ? t("sur tout l'historique", 'across all history')
              : t(`sur ${periodDays} j`, `over ${periodDays} d`)}
          </span>
        </div>
        <div className="space-y-2 p-3 md:flex-1 md:overflow-y-auto">
          {reels.length === 0 ? (
            <Empty title={t('Aucun reel', 'No reels')} className="py-6" />
          ) : (
            reels.map((r) => <ReelCard key={r.reelId} reel={r} />)
          )}
        </div>
      </aside>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-3">
      {children}
    </h2>
  );
}

function StatsRow({ summary }: { summary: TemplateDetail['summary'] }) {
  const { t } = useI18n();
  return (
    <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <StatCell label={t('Médiane', 'Median')} value={formatNumber(summary.medianViews)} hero />
      <StatCell label={t('Meilleur', 'Best')} value={formatNumber(summary.maxViews)} />
      <StatCell label="Minimum" value={formatNumber(summary.minViews)} />
      <StatCell label={t('Total vues', 'Total views')} value={formatNumber(summary.totalViews)} />
    </dl>
  );
}

function StatCell({ label, value, hero }: { label: string; value: string; hero?: boolean }) {
  return (
    <div className="min-w-0 rounded-[12px] border border-line-1 bg-bg-2 px-3 py-2">
      <dt className="text-[10px] font-medium uppercase tracking-[0.14em] text-text-4">{label}</dt>
      <dd
        className={
          hero
            ? 'font-display mt-0.5 text-[22px] tabular-nums text-violet-deep'
            : 'font-display mt-0.5 text-[18px] tabular-nums text-text-1'
        }
      >
        {value}
      </dd>
    </div>
  );
}

function AccountBreakdownTable({
  rows,
  identityMap,
}: {
  rows: TemplateAccountBreakdown[];
  identityMap: Map<string, IdentityResponse>;
}) {
  const { t } = useI18n();
  if (rows.length === 0) {
    return <Empty title={t('Aucun compte', 'No accounts')} className="py-6" />;
  }

  return (
    <div className="max-w-full overflow-x-auto rounded-[12px] border border-line-1">
      <table className="w-full min-w-[540px] text-[12px]">
        <thead className="bg-bg-2 text-[10px] uppercase tracking-[0.16em] text-text-4">
          <tr>
            <Th>{t('Compte', 'Account')}</Th>
            <Th>{t('Identité', 'Identity')}</Th>
            <Th align="right">Reels</Th>
            <Th align="right">{t('Médiane', 'Median')}</Th>
            <Th align="right">{t('Meilleur', 'Best')}</Th>
            <Th align="right">{t('Viraux', 'Viral')}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-1">
          {rows.map((r) => {
            const identity = r.identityId ? identityMap.get(r.identityId) : null;
            return (
              <tr key={r.accountId} className="text-text-2">
                <Td>
                  {r.username ? (
                    <Link
                      to={`/accounts/${r.accountId}`}
                      className="font-medium text-text-1 hover:text-violet-deep"
                    >
                      @{r.username}
                    </Link>
                  ) : (
                    <span className="text-text-4">—</span>
                  )}
                </Td>
                <Td>
                  {identity ? <Chip>{identity.name}</Chip> : <span className="text-text-4">—</span>}
                </Td>
                <Td align="right">{r.reelCount}</Td>
                <Td align="right" className="font-display tabular-nums">
                  {formatNumber(r.medianViews)}
                </Td>
                <Td align="right" className="tabular-nums">
                  {formatNumber(r.maxViews)}
                </Td>
                <Td align="right">
                  {r.viralCount > 0 ? (
                    <span className="inline-flex items-center gap-1 font-medium text-violet-deep">
                      <Flame className="h-3 w-3" />
                      {r.viralCount}
                    </span>
                  ) : (
                    <span className="text-text-4">—</span>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={`px-3 py-2 font-semibold ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  className,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <td
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className ?? ''}`}
    >
      {children}
    </td>
  );
}

function ReelCard({ reel }: { reel: ReelResponse }) {
  useI18n();
  const thumbnail = reel.thumbnailUrl;
  return (
    <a
      href={buildReelUrl(reel.shortcode)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-stretch gap-3 rounded-[10px] border border-line-1 bg-bg-1 p-2 transition-colors hover:border-line-2"
    >
      <div className="relative h-16 w-12 shrink-0 overflow-hidden rounded-[8px] bg-bg-2">
        {thumbnail ? (
          <img
            src={proxyImage(thumbnail)}
            alt={reel.shortcode}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-1">
        <div className="flex items-center gap-1 text-[11px] text-text-3">
          <span className="truncate font-mono">{reel.shortcode}</span>
          {reel.viralFlaggedAt && (
            <Chip tone="violet" className="ml-auto">
              <Flame className="h-2.5 w-2.5" />
              Viral
            </Chip>
          )}
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-display flex items-center gap-1 text-[15px] tabular-nums text-text-1">
            <Eye className="h-3 w-3 text-text-3" />
            {formatNumber(reel.viewsCount)}
          </span>
          <span className="inline-flex items-center gap-0.5 text-[10px] text-text-4">
            {formatDate(reel.publishedAt, 'dd MMM')}
            <ExternalLink className="h-2.5 w-2.5" />
          </span>
        </div>
      </div>
    </a>
  );
}
